import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FileUpload } from 'primeng/fileupload';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { Router } from '@angular/router';
import { AppConfig } from 'src/app/app.config';
import { SpinnerService } from 'src/core/services/spinner.service';
import { SentryService } from 'src/core/services/sentry.service';
import { OrganizationService } from 'src/core/services/organization.service';

@Component({
  selector: 'app-profile-settings',
  templateUrl: './profile-settings.component.html',
  styleUrls: ['./profile-settings.component.scss'],
})
export class ProfileSettingsComponent implements OnInit {
  @ViewChild('fileUpload', { static: false }) FileUpload!: FileUpload;

  uploadedImage!: string;
  ProfileForm!: FormGroup;
  uploadedFile: any;
  IsNewImageUploaded: boolean = false;
  public config = AppConfig.settings;

  userdata: any = JSON.parse(localStorage.getItem('currentUser')!);
  user = this.userdata.user;
  isdisable!: boolean;
  profileImage: any;
  loading: boolean = false;
  userAcceptCookiesTerms: boolean = this.userdata.userCookiesDetails.isUserCookiesAccepted;
  
  // Modal visibility flags
  showTermsModal: boolean = false;
  showCookiesModal: boolean = false;
  
  // Content for modals
  termsContent: any = null;
  cookiesContent: any = null;
  
  // Flag to enable or disable Sentry
  enableSentry: boolean = AppConfig.settings.enableSentry;
  
  constructor(
    private fb: FormBuilder,
    private AuthenticationService: AuthenticationService,
    private router: Router,
    private messageService: MessageService,
    private spinnerService: SpinnerService,
    private sentryService: SentryService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit() {
    this.AuthenticationService.isProfileSettings.next(true);
    this.initForms();
    this.loadTermsAndCookiesContent();
  }

  // Load both terms and cookies content during initialization
  loadTermsAndCookiesContent() {
    //this.spinnerService.show();
    
    // Load terms content
    this.AuthenticationService.getActiveAcceptanceService(this.userdata.board.organisationId)
      .subscribe({
        next: (response: any) => {
          this.termsContent = {
            title: response.name || 'Terms & Conditions',
            version: response.version || '1.0',
            text: response.text || 'No terms content available.'
          };
          checkLoadingComplete();
        },
        error: (error) => {
          console.error('Error fetching terms:', error);
          this.termsContent = {
            title: 'Terms & Conditions',
            version: '1.0',
            text: 'Unable to load terms content. Please try again later.'
          };
          checkLoadingComplete();
        }
      });
      
    // Load cookies content
    this.AuthenticationService.getActiveCookieTermsService(this.userdata.board.organisationId)
      .subscribe({
        next: (response: any) => {
          this.cookiesContent = {
            title: response.name || 'Cookie Policy',
            version: response.version || '1.0',
            text: response.text || 'No cookie policy content available.'
          };
          checkLoadingComplete();
        },
        error: (error) => {
          console.error('Error fetching cookie policy:', error);
          this.cookiesContent = {
            title: 'Cookie Policy',
            version: '1.0',
            text: 'Unable to load cookie policy content. Please try again later.'
          };
          checkLoadingComplete();
        }
      });
    
    // Helper function to hide spinner when both requests complete
    let loadedCount = 0;
    const checkLoadingComplete = () => {
      loadedCount++;
      if (loadedCount === 2) {
        //this.spinnerService.hide();
      }
    };
  }

  initForms() {
    this.ProfileForm = this.fb.group({
      FirstName: [
        this.user.firstName,
        [Validators.required, Validators.pattern(/^[a-zA-ZÆØÅæøå\s]*$/)],
      ],
      LastName: [
        this.user.lastName,
        [Validators.required, Validators.pattern(/^[a-zA-ZÆØÅæøå\s]*$/)],
      ],
      Email: [this.user.email, Validators.required],
      PhoneNumber: [this.user.phoneNumber],
      JobTitle: [this.user.position],
      hasImage: [this.user.hasImage],
    });

    if (this.user.hasImage) {
      this.uploadedImage =
        this.config.apiServer.awsS3URL +
        `/users/` +
        this.user.id +
        `/static/image-190x190.jpg`;
    }
  }

  // Open Terms & Conditions modal - now just toggles visibility
  openTermsModal() {
    this.showTermsModal = true;
  }

  // Open Cookie Policy modal - now just toggles visibility
  openCookiesModal() {
    this.showCookiesModal = true;
  }
  
  // Close modals
  closeTermsModal() {
    this.showTermsModal = false;
  }
  
  closeCookiesModal() {
    this.showCookiesModal = false;
  }

  onCustomLinkClick() {
    this.FileUpload.choose();
  }

  onSelectImage(file: any) {
    this.uploadedFile = file;
    this.AuthenticationService.imageUploadRequest(file).subscribe(
      (res: any) => {
        this.IsNewImageUploaded = true;
        this.uploadedImage = res.Uri;
      }
    );
  }

  onchange() {
    if (this.ProfileForm.valid) {
      this.isdisable = false;
    } else {
      this.isdisable = true;
    }
  }

  submitForm() {
    //this.spinnerService.show();
    this.loading = true;
    this.AuthenticationService.submitProfileForm(
      this.userdata,
      this.ProfileForm,
      this.uploadedImage,
      this.IsNewImageUploaded
    ).subscribe((res: any) => {
      this.AuthenticationService.getCurrentUserDetails(
        this.userdata.authenticated.access_token
      ).subscribe((currentUser: any) => {
        // update user in localStorage
        // update user email in the metafire
        this.AuthenticationService.updateMetafireDUserEmail(
          currentUser.id,
          this.ProfileForm.value.Email,
          this.ProfileForm.value.FirstName +
            ' ' +
            this.ProfileForm.value.LastName,
          this.userdata.board.id,
          null
        ).subscribe((res: any) => {
          //this.spinnerService.hide();
          this.loading = false;
          this.refreshLocalStorage(currentUser);
          location.reload();
          this.messageService.add({
            severity: 'success',
            summary: 'User Profile',
            detail: `Your profile has been successfully updated.`,
            life: 3000,
          });
        });
      });
    });
  }

  refreshLocalStorage(currentUser: any) {
    let currentUserLocalStoage = this.userdata;
    currentUserLocalStoage.user.firstName = currentUser.firstName;
    currentUserLocalStoage.user.lastName = currentUser.lastName;
    currentUserLocalStoage.user.email = currentUser.email;
    currentUserLocalStoage.user.position = currentUser.position;
    currentUserLocalStoage.user.phoneNumber = currentUser.phoneNumber;
    currentUserLocalStoage.user.hasImage = this.uploadedImage
      ? currentUser.hasImage
      : false;

    localStorage.clear();
    localStorage.setItem('currentUser', JSON.stringify(currentUserLocalStoage));
    this.AuthenticationService.initializeAgentMode();
    this.organizationService.setOrganizationIdInStorage(currentUserLocalStoage);
  }

  routing() {
    this.router.navigate(['/reports']);
  }

  removeImage() {
    this.uploadedImage = '';
    this.AuthenticationService.ProfileImage.next('');
  }

  acceptCookies() {
    // Prevent multiple calls if already loading
    if (this.loading) return;
    
    this.AuthenticationService.acceptUserCookie(
      this.user.id,
      this.userdata.userCookiesDetails.activeCookiesId,
      this.userAcceptCookiesTerms 
    ).subscribe({
      next: (res: any) => {
        // Update the local storage to persist the user's choice
        const currentUserData = JSON.parse(localStorage.getItem('currentUser')!);
        if (currentUserData && currentUserData.userCookiesDetails) {
          currentUserData.userCookiesDetails.isUserCookiesAccepted = this.userAcceptCookiesTerms;
          localStorage.setItem('currentUser', JSON.stringify(currentUserData.rightsHolder.tr));
          this.AuthenticationService.initializeAgentMode();

        }
        
        // Initialize Sentry if cookies are accepted and it's enabled in config
        if (this.userAcceptCookiesTerms && this.enableSentry && !this.sentryService.isSentryInitialized()) {
          this.sentryService.initializeSentry();
          
          // Set user information for better tracking
          this.sentryService.setUser({
            id: this.user.id,
            email: this.user.email,
            username: `${this.user.firstName} ${this.user.lastName}`,
            board: `${this.user.board.title}`
          });
          
        } else if (!this.userAcceptCookiesTerms) {
          // If cookies are rejected, ensure Sentry user tracking is cleared
          this.sentryService.clearUser();
        }
        
        this.messageService.add({
          severity: 'success',
          summary: 'Cookies Policy',
          detail: this.userAcceptCookiesTerms ? 
            'Cookies policy has been accepted successfully' : 
            'Cookies policy preferences updated successfully',
          life: 3000,
        });
        
      },
      error: (error) => {
        // Revert the UI state if the API call fails
        this.userAcceptCookiesTerms = !this.userAcceptCookiesTerms;
        this.messageService.add({
          severity: 'error',
          summary: 'Cookies Policy',
          detail: 'Failed to update cookie preferences. Please try again.',
          life: 3000,
        });
      }
    });
  }

  toggleCookiesConsent() {
    this.acceptCookies();
  }

  manualToggle(event: Event) {
    event.preventDefault();
    
    this.userAcceptCookiesTerms = !this.userAcceptCookiesTerms;
    
    this.acceptCookies();
  }
}



